import React, { useMemo } from "react";
import { Legend, LegendProps } from "recharts";
import { Box, Text, HStack, Wrap } from "@chakra-ui/react";
import { BarData, ColorScheme } from "./interface";

export interface LegendItem {
  label: string;
  value?: string | number;
  color?: string;
  key?: string;
  labelProps?: React.HTMLAttributes<HTMLElement>;
  valueProps?: React.HTMLAttributes<HTMLElement>;
  colorBoxProps?: React.HTMLAttributes<HTMLElement>;
}

export interface CustomLegendProps {
  payload?: Array<{
    value?: string;
    type?: string;
    id?: string;
    color?: string;
    [key: string]: any;
  }>;
  data?: BarData[];
  items?: LegendItem[];
  colors?: string[];
  [key: string]: any;
}

export interface ChartLegendConfig {
  enabled?: boolean;
  customContent?: (props: CustomLegendProps) => React.ReactNode;
  items?: LegendItem[];
  dataKey?: string;
  labelKey?: string;
  colorKey?: string;
  generateColors?: boolean;
  colors?: string[];
  wrapperStyle?: React.CSSProperties;
  [key: string]: any;
}

export interface ChartLegendProps {
  legend?: ChartLegendConfig;
  data?: BarData[];
  series?: Array<{
    dataKey: string;
    name: string;
    fill?: string;
    [key: string]: any;
  }>;
  colorScheme?: ColorScheme;
}

export const ChartLegend: React.FC<ChartLegendProps> = ({
  legend,
  data,
  colorScheme,
}) => {
  const isLegendEnabled = legend?.enabled !== false;

  const legendStyle = colorScheme?.legend || colorScheme?.series?.legend;
  const defaultLegendFill = legendStyle?.fill;
  const defaultLegendFontSize = legendStyle?.fontSize;
  const defaultLegendFontWeight = legendStyle?.fontWeight;

  const autoGeneratedItems = useMemo((): LegendItem[] | null => {
    if (!isLegendEnabled) {
      return null;
    }

    if (
      !legend?.labelKey ||
      !legend?.generateColors ||
      !legend?.colors ||
      !data?.length
    ) {
      return null;
    }

    return data.map((entry: any, index: number) => ({
      label: entry[legend.labelKey!] || `Item ${index + 1}`,
      color: legend.colors![index] || "transparent",
      key: String(entry[legend.colorKey || "id"] || `legend-item-${index}`),
    }));
  }, [legend?.labelKey, legend?.generateColors, legend?.colors, data]);

  const defaultLegendProps: LegendProps = {
    content: legend?.customContent
      ? (props: any) =>
          legend.customContent?.({
            payload: props.payload,
            data: data || [],
            items: legend.items || autoGeneratedItems || [],
            colors: legend?.colors,
            ...props,
          })
      : autoGeneratedItems || legend?.items
      ? (props: any) => {
          const items = (autoGeneratedItems ||
            legend?.items ||
            []) as LegendItem[];
          return (
            <Wrap gap={4} mt={4} style={legend?.wrapperStyle}>
              {items.map((item, index) => (
                <HStack key={item.key || `legend-item-${index}`} gap={2}>
                  {item.color && (
                    <Box
                      w={3}
                      h={3}
                      borderRadius="xs"
                      bg={item.color}
                      {...(item.colorBoxProps || {})}
                    />
                  )}
                  <Text
                    fontSize={defaultLegendFontSize || "xs"}
                    color={defaultLegendFill}
                    fontWeight={defaultLegendFontWeight}
                    {...(item.labelProps || {})}
                  >
                    {item.label}
                  </Text>
                  {item.value !== undefined && (
                    <Text
                      fontSize={defaultLegendFontSize || "xs"}
                      color={defaultLegendFill}
                      fontWeight={defaultLegendFontWeight}
                      {...(item.valueProps || {})}
                    >
                      : {item.value}
                    </Text>
                  )}
                </HStack>
              ))}
            </Wrap>
          );
        }
      : undefined,
    wrapperStyle: legend?.wrapperStyle || { color: "gray", fontSize: "8px" },
    ...Object.fromEntries(
      Object.entries(legend || {}).filter(
        ([key]) =>
          ![
            "enabled",
            "customContent",
            "items",
            "dataKey",
            "labelKey",
            "colorKey",
            "generateColors",
            "colors",
            "wrapperStyle",
          ].includes(key)
      )
    ),
  };

  return <Legend {...defaultLegendProps} />;
};
